import React, { useLayoutEffect, useRef, useState } from "react";
import { motion, useMotionValueEvent, useScroll, useTransform } from "framer-motion";
import { BellRing, CalendarCheck, Check, Gift, ShoppingBag } from "lucide-react";

import { COURSES, SHOP } from "./data";
import ShopMini, { PHONE, ScaledStage } from "./ShopMini";
import { EASE, Em, SectionTag, euro, useIsDesktop } from "./primitives";

/* ------------------------------------------------------------------ */
/*  Mini-voorbeelden per gang                                          */
/* ------------------------------------------------------------------ */

// Elke mock speelt af via varianten "off" → "on"; de kaart zelf start ze (zie CourseCard).

function MockWebshop() {
   const url = `resto-online.be/${SHOP.slug}`;
   return (
      <div className="flex h-full flex-col">
         <div className="flex items-center gap-2 rounded-full bg-white px-3 py-2 shadow-[0_1px_0_rgba(18,17,16,0.06)]">
            <span className="h-2 w-2 rounded-full bg-emerald-500" />
            <motion.span
               variants={{ off: { clipPath: "inset(0 100% 0 0)" }, on: { clipPath: "inset(0 0% 0 0)" } }}
               transition={{ duration: 1.4, ease: (t) => Math.floor(t * url.length) / url.length, delay: 0.3 }}
               className="font-ticket text-[11px] text-lp-ink"
            >
               {url}
            </motion.span>
            <motion.span
               className="h-3.5 w-px bg-lp-ink"
               animate={{ opacity: [1, 0, 1] }}
               transition={{ duration: 1, repeat: Infinity }}
            />
         </div>
         <motion.div
            variants={{ off: { y: 30, opacity: 0 }, on: { y: 0, opacity: 1 } }}
            transition={{ duration: 0.9, ease: EASE, delay: 1.5 }}
            className="mt-3 min-h-0 flex-1 overflow-hidden rounded-xl bg-white shadow-[0_20px_40px_-24px_rgba(18,17,16,0.4)]"
         >
            <ScaledStage pick={() => PHONE} className="h-full" style={{ height: "100%" }}>
               {() => <ShopMini layout="phone" />}
            </ScaledStage>
         </motion.div>
      </div>
   );
}

function MockSlots() {
   // Wat er de komende dagen al gepland staat, per afhaalmoment
   const rows = [
      { t: "vr 18:30", n: 6 },
      { t: "za 12:00", n: 4 },
      { t: "za 18:30", n: 3 },
      { t: "zo 12:00", n: 1 },
   ];
   const most = Math.max(...rows.map((r) => r.n));
   return (
      <div className="flex h-full flex-col justify-center gap-3">
         {rows.map((r, i) => (
            <div key={r.t} className="flex items-center gap-3">
               <span className="w-[4.2rem] font-ticket text-[12px] text-lp-ink">{r.t}</span>
               <div className="relative h-8 flex-1 overflow-hidden rounded-lg bg-white">
                  <motion.div
                     variants={{ off: { width: "0%" }, on: { width: `${(r.n / most) * 100}%` } }}
                     transition={{ duration: 1.1, ease: EASE, delay: 0.2 + i * 0.12 }}
                     className="h-full rounded-lg bg-lp-ink"
                  />
               </div>
               <span className="w-14 text-right font-ticket text-[11px] text-lp-muted">{r.n} best.</span>
            </div>
         ))}
         <p className="mt-2 font-ticket text-[10px] uppercase tracking-[0.14em] text-lp-muted">Al gepland · dagen vooraf</p>
      </div>
   );
}

function MockDelivery() {
   const zones = [
      { pc: "9000", city: "Gent", price: 2.5 },
      { pc: "9050", city: "Gentbrugge", price: 3 },
      { pc: "9040", city: "Sint-Amandsberg", price: 3.5 },
   ];
   return (
      <div className="flex h-full flex-col">
         <div className="relative mx-auto aspect-square w-[62%] max-w-[180px]">
            {[1, 0.7, 0.42].map((s, i) => (
               <motion.span
                  key={s}
                  variants={{ off: { scale: 0, opacity: 0 }, on: { scale: s, opacity: 1 } }}
                  transition={{ duration: 0.9, ease: EASE, delay: 0.5 - i * 0.15 }}
                  className="absolute inset-0 rounded-full border border-dashed border-lp-ink/25 bg-lp-blue/[0.04]"
               />
            ))}
            <span className="absolute left-1/2 top-1/2 flex h-7 w-7 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-lp-ink font-editorial text-sm italic text-lp-paper">
               {SHOP.initial}
            </span>
            {[
               ["22%", "30%"],
               ["72%", "38%"],
               ["58%", "80%"],
            ].map(([l, t], i) => (
               <motion.span
                  key={i}
                  className="absolute h-2.5 w-2.5 rounded-full bg-lp-blue ring-4 ring-lp-blue/20"
                  style={{ left: l, top: t }}
                  animate={{ scale: [1, 1.35, 1] }}
                  transition={{ duration: 1.6, repeat: Infinity, delay: i * 0.4 }}
               />
            ))}
         </div>
         <ul className="mt-auto space-y-1.5 pt-4">
            {zones.map((z) => (
               <li key={z.pc} className="flex items-baseline rounded-lg bg-white px-3 py-2 text-[12px] text-lp-ink">
                  <span className="font-ticket font-bold">{z.pc}</span>
                  <span className="ml-2 text-lp-muted">{z.city}</span>
                  <span className="lp-leader" />
                  <span className="font-medium tabular-nums">{euro(z.price)}</span>
               </li>
            ))}
         </ul>
      </div>
   );
}

function MockPayments() {
   return (
      <div className="relative flex h-full items-center justify-center">
         <motion.div
            variants={{ off: { rotate: 0, x: 0 }, on: { rotate: -10, x: -34 } }}
            transition={{ duration: 1, ease: EASE, delay: 0.3 }}
            className="absolute aspect-[1.6] w-[70%] rounded-2xl bg-gradient-to-br from-[#2B2A28] to-lp-ink p-4 text-lp-paper shadow-xl"
         >
            <p className="font-ticket text-[10px] uppercase tracking-[0.16em] text-lp-paper/50">Kaart</p>
            <p className="absolute bottom-4 left-4 font-ticket text-[11px] tracking-[0.2em]">•••• 4242</p>
         </motion.div>
         <motion.div
            variants={{ off: { rotate: 0, x: 0, y: 0 }, on: { rotate: 6, x: 26, y: 14 } }}
            transition={{ duration: 1, ease: EASE, delay: 0.3 }}
            className="absolute aspect-[1.6] w-[70%] rounded-2xl bg-gradient-to-br from-[#3D7BFF] to-lp-blue p-4 text-white shadow-2xl"
         >
            <p className="text-sm font-semibold">Bancontact</p>
            <div className="absolute bottom-4 right-4 grid grid-cols-5 gap-[3px]">
               {Array.from({ length: 25 }).map((_, i) => (
                  <span key={i} className={`h-[5px] w-[5px] ${[0, 2, 4, 6, 7, 11, 13, 17, 18, 20, 22, 24].includes(i) ? "bg-white" : "bg-white/25"}`} />
               ))}
            </div>
         </motion.div>
         <motion.div
            variants={{ off: { y: 20, opacity: 0, scale: 0.9 }, on: { y: 0, opacity: 1, scale: 1 } }}
            transition={{ duration: 0.6, ease: EASE, delay: 1.1 }}
            className="absolute bottom-0 flex items-center gap-2 rounded-full bg-white px-4 py-2 text-[12px] font-medium text-lp-ink shadow-lg"
         >
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-emerald-500 text-white">
               <Check size={12} strokeWidth={3} />
            </span>
            {euro(38.5)} → jouw rekening
         </motion.div>
      </div>
   );
}

function MockDiscounts() {
   return (
      <div className="flex h-full flex-col items-center justify-center gap-5">
         <motion.div
            variants={{ off: { rotate: 0, scale: 0.9, opacity: 0 }, on: { rotate: -4, scale: 1, opacity: 1 } }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.2 }}
            className="relative flex w-[85%] items-stretch overflow-hidden rounded-xl bg-lp-tomato text-white"
         >
            <span className="absolute -left-2.5 top-1/2 h-5 w-5 -translate-y-1/2 rounded-full bg-lp-paper" />
            <span className="absolute -right-2.5 top-1/2 h-5 w-5 -translate-y-1/2 rounded-full bg-lp-paper" />
            <div className="flex-1 border-r border-dashed border-white/40 px-5 py-4">
               <p className="font-ticket text-[10px] uppercase tracking-[0.16em] text-white/70">Kortingscode</p>
               <p className="mt-1 font-ticket text-lg font-bold tracking-[0.08em]">VRIJDAG10</p>
            </div>
            <div className="flex items-center px-5 font-editorial text-3xl italic">−10%</div>
         </motion.div>
         <div className="flex items-baseline gap-3 text-lp-ink">
            <span className="relative text-lg text-lp-muted">
               {euro(22.5)}
               <motion.span
                  variants={{ off: { scaleX: 0 }, on: { scaleX: 1 } }}
                  transition={{ duration: 0.5, delay: 1 }}
                  className="absolute left-0 right-0 top-1/2 h-[2px] origin-left bg-lp-tomato"
               />
            </span>
            <motion.span
               variants={{ off: { opacity: 0, y: 8 }, on: { opacity: 1, y: 0 } }}
               transition={{ duration: 0.5, delay: 1.3 }}
               className="text-2xl font-semibold"
            >
               {euro(20.25)}
            </motion.span>
         </div>
      </div>
   );
}

function MockGiftCard() {
   return (
      <div className="flex h-full items-center justify-center [perspective:900px]">
         <motion.div
            animate={{ rotateY: [-16, 16, -16], rotateX: [6, -4, 6] }}
            transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
            className="relative aspect-[1.6] w-[82%] overflow-hidden rounded-2xl bg-lp-blue p-5 text-white shadow-[0_30px_50px_-20px_rgba(31,92,255,0.6)] [transform-style:preserve-3d]"
         >
            <span className="absolute -right-10 -top-10 h-32 w-32 rounded-full bg-white/10" />
            <span className="absolute -bottom-16 -left-6 h-32 w-32 rounded-full bg-white/[0.07]" />
            <div className="relative flex items-center justify-between">
               <span className="font-ticket text-[10px] uppercase tracking-[0.18em] text-white/70">Cadeaubon</span>
               <Gift size={18} className="text-white/80" />
            </div>
            <p className="relative mt-4 font-editorial text-5xl italic leading-none">€50</p>
            <p className="relative mt-1 text-[12px] text-white/70">{SHOP.name}</p>
            <p className="absolute bottom-4 right-5 font-ticket text-[10px] tracking-[0.16em] text-white/70">PLNT-7788</p>
         </motion.div>
      </div>
   );
}

function MockReservations() {
   const days = Array.from({ length: 35 }, (_, i) => i - 2);
   return (
      <div className="flex h-full flex-col">
         <div className="flex items-baseline justify-between">
            <p className="text-sm font-semibold text-lp-ink">Oktober</p>
            <p className="font-ticket text-[10px] uppercase tracking-[0.14em] text-lp-muted">Tafel reserveren</p>
         </div>
         <div className="mt-3 grid grid-cols-7 gap-1 text-center font-ticket text-[10px]">
            {["M", "D", "W", "D", "V", "Z", "Z"].map((d, i) => (
               <span key={i} className="text-lp-muted">{d}</span>
            ))}
            {days.map((d, i) => {
               const valid = d > 0 && d <= 31;
               const closed = valid && i % 7 === 0; // maandag gesloten
               const picked = d === 17;
               return (
                  <motion.span
                     key={i}
                     variants={{ off: { opacity: 0 }, on: { opacity: 1 } }}
                     transition={{ delay: 0.15 + i * 0.012 }}
                     className={`relative flex aspect-square items-center justify-center rounded-md ${
                        !valid ? "" : picked ? "font-bold text-white" : closed ? "text-lp-ink/25 line-through" : "bg-white text-lp-ink"
                     }`}
                  >
                     {picked && (
                        <motion.span
                           variants={{ off: { scale: 0 }, on: { scale: 1 } }}
                           transition={{ delay: 0.9, type: "spring", stiffness: 300, damping: 15 }}
                           className="absolute inset-0 rounded-md bg-lp-blue"
                        />
                     )}
                     <span className="relative">{valid ? d : ""}</span>
                  </motion.span>
               );
            })}
         </div>
         <motion.div
            variants={{ off: { opacity: 0, y: 12 }, on: { opacity: 1, y: 0 } }}
            transition={{ duration: 0.6, ease: EASE, delay: 1.2 }}
            className="mt-auto flex items-center gap-3 rounded-xl bg-white p-3"
         >
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-lp-blue text-white">
               <CalendarCheck size={16} />
            </span>
            <span className="leading-tight">
               <span className="block text-[13px] font-semibold text-lp-ink">Vr 17 okt · 20:00</span>
               <span className="block text-[11px] text-lp-muted">4 personen · bevestigd</span>
            </span>
         </motion.div>
      </div>
   );
}

function MockDashboard() {
   const notes = [
      { icon: ShoppingBag, t: "Nieuwe bestelling #0151", s: `${euro(42)} · afhalen 18:30`, c: "bg-lp-blue" },
      { icon: CalendarCheck, t: "Nieuwe reservatie", s: "4 personen · vr 20:00", c: "bg-emerald-500" },
      { icon: Gift, t: "Cadeaubon verkocht", s: `${euro(50)} · PLNT-7788`, c: "bg-lp-tomato" },
   ];
   return (
      <div className="flex h-full flex-col justify-center gap-2.5">
         {notes.map((n, i) => (
            <motion.div
               key={n.t}
               variants={{ off: { opacity: 0, y: -24, scale: 0.95 }, on: { opacity: 1, y: 0, scale: 1 } }}
               transition={{ duration: 0.6, ease: EASE, delay: 0.3 + i * 0.45 }}
               className="flex items-center gap-3 rounded-2xl bg-white/[0.08] p-3 ring-1 ring-white/10 backdrop-blur"
            >
               <span className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl text-white ${n.c}`}>
                  <n.icon size={16} />
               </span>
               <span className="min-w-0 flex-1 leading-tight">
                  <span className="block truncate text-[13px] font-semibold text-lp-paper">{n.t}</span>
                  <span className="block truncate text-[11px] text-lp-paper/50">{n.s}</span>
               </span>
               <span className="font-ticket text-[10px] text-lp-paper/40">nu</span>
            </motion.div>
         ))}
         <div className="mt-2 flex items-center gap-2 font-ticket text-[10px] uppercase tracking-[0.14em] text-lp-paper/40">
            <BellRing size={12} /> Pushmeldingen op je gsm
         </div>
      </div>
   );
}

const MOCKS = {
   webshop: MockWebshop,
   slots: MockSlots,
   delivery: MockDelivery,
   payments: MockPayments,
   discounts: MockDiscounts,
   giftcards: MockGiftCard,
   reservations: MockReservations,
   dashboard: MockDashboard,
};

/* ------------------------------------------------------------------ */
/*  Kaart per gang                                                     */
/* ------------------------------------------------------------------ */

function CourseCard({ c, stacked = false }) {
   const Mock = MOCKS[c.id];
   const dark = c.id === "dashboard";
   return (
      <article
         className={`group flex flex-col rounded-[28px] p-6 sm:p-7 ${
            stacked ? "w-full shadow-[0_-18px_40px_-24px_rgba(18,17,16,0.35)]" : "h-[min(76vh,640px)] w-[400px] shrink-0"
         } ${dark ? "lp-grain lp-grain-dark relative bg-lp-ink text-lp-paper" : "border border-lp-ink/10 bg-lp-card text-lp-ink"}`}
      >
         <div className="flex items-start justify-between">
            <p className="font-editorial text-2xl italic">{c.course}</p>
            {c.badge && (
               <span className="rounded-full bg-lp-blue px-3 py-1 font-ticket text-[10px] font-bold uppercase tracking-[0.14em] text-white">
                  {c.badge}
               </span>
            )}
         </div>

         <motion.div
            initial="off"
            whileInView="on"
            viewport={{ once: true, amount: 0.5 }}
            className={`mt-5 overflow-hidden rounded-2xl p-4 ${stacked ? "h-[250px]" : "min-h-0 flex-1"} ${dark ? "bg-white/[0.04]" : "bg-lp-paper"}`}
         >
            <Mock />
         </motion.div>

         <h3 className="mt-6 text-[26px] font-semibold leading-tight tracking-[-0.035em]">{c.title}</h3>
         <p className={`mt-2 min-h-[4.9rem] text-[15px] leading-relaxed ${dark ? "text-lp-paper/60" : "text-lp-muted"}`}>{c.body}</p>
      </article>
   );
}

function MenuIntro({ desktop = false }) {
   return (
      <div className={desktop ? "flex h-[min(76vh,640px)] w-[560px] shrink-0 flex-col justify-between py-2 pr-10" : "mx-auto max-w-[560px] px-5 sm:px-8"}>
         <SectionTag>Het menu</SectionTag>
         <div className={desktop ? "" : "mt-10"}>
            <h2 className="text-[clamp(2.8rem,6vw,5.6rem)] font-semibold leading-[0.92] tracking-[-0.05em] text-lp-ink">
               Acht gangen.
               <br />
               <Em className="text-lp-blue">Eén abonnement.</Em>
            </h2>
            <p className="mt-6 max-w-sm text-lg leading-relaxed text-lp-muted">
               Alles wat je zaak online nodig heeft, netjes in de juiste volgorde opgediend.
            </p>
         </div>
         <p className="mt-8 flex items-center gap-3 font-ticket text-[11px] uppercase tracking-[0.18em] text-lp-muted">
            <motion.span animate={desktop ? { x: [0, 8, 0] } : { y: [0, 6, 0] }} transition={{ duration: 1.6, repeat: Infinity }}>
               {desktop ? "→" : "↓"}
            </motion.span>
            {desktop ? "Scroll om te proeven" : "Scroll, gang per gang"}
         </p>
      </div>
   );
}

/* ------------------------------------------------------------------ */
/*  Gsm: de gangen worden één voor één opgediend, als een stapel borden */
/* ------------------------------------------------------------------ */

function StackedCourse({ c, i, total, progress }) {
   // Zodra de volgende gang erover schuift, zakt deze een beetje weg
   const behind = total - 1 - i;
   const scale = useTransform(progress, [i / total, 1], [1, 1 - behind * 0.03]);
   const dim = useTransform(progress, [i / total, 1], [0, Math.min(0.35, behind * 0.06)]);
   return (
      <div className="sticky mb-6 last:mb-0" style={{ top: `calc(7.25rem + ${i * 10}px)` }}>
         <motion.div style={{ scale }} className="relative origin-top">
            <CourseCard c={c} stacked />
            <motion.span aria-hidden style={{ opacity: dim }} className="pointer-events-none absolute inset-0 rounded-[28px] bg-lp-ink" />
         </motion.div>
      </div>
   );
}

function CourseStack() {
   const ref = useRef(null);
   const { scrollYProgress } = useScroll({ target: ref, offset: ["start 116px", "end end"] });
   return (
      <div ref={ref} className="relative mx-auto mt-12 max-w-[560px] px-5 sm:px-8">
         {COURSES.map((c, i) => (
            <StackedCourse key={c.id} c={c} i={i} total={COURSES.length} progress={scrollYProgress} />
         ))}
      </div>
   );
}

/* ------------------------------------------------------------------ */
/*  Sectie: verticaal scrollen = horizontaal door het menu             */
/* ------------------------------------------------------------------ */

export default function MenuCourses() {
   const sectionRef = useRef(null);
   const trackRef = useRef(null);
   const desktop = useIsDesktop();
   const [distance, setDistance] = useState(0);
   const [active, setActive] = useState(0);

   useLayoutEffect(() => {
      if (!desktop) return;
      const measure = () => {
         const t = trackRef.current;
         if (t) setDistance(Math.max(0, t.scrollWidth - window.innerWidth));
      };
      measure();
      const ro = new ResizeObserver(measure);
      ro.observe(trackRef.current);
      window.addEventListener("resize", measure);
      return () => {
         ro.disconnect();
         window.removeEventListener("resize", measure);
      };
   }, [desktop]);

   const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end end"] });
   const x = useTransform(scrollYProgress, (v) => -v * distance);
   const ghostX = useTransform(scrollYProgress, (v) => -v * distance * 0.35);

   useMotionValueEvent(scrollYProgress, "change", (v) => {
      setActive(Math.min(COURSES.length - 1, Math.max(0, Math.round(v * (COURSES.length - 1)))));
   });

   return (
      <section
         id="menu"
         ref={sectionRef}
         className="lp-grain relative bg-lp-paper"
         style={desktop ? { height: `calc(100vh + ${distance}px)` } : undefined}
      >
         <div className={desktop ? "sticky top-0 flex h-screen flex-col justify-center overflow-hidden" : "py-24"}>
            {/* spookwoord op de achtergrond */}
            {desktop && (
               <motion.p
                  aria-hidden
                  style={{ x: ghostX }}
                  className="lp-outline pointer-events-none absolute bottom-[-0.18em] left-0 whitespace-nowrap font-editorial text-[26vw] italic leading-none text-lp-ink/[0.07]"
               >
                  à la carte · à la carte
               </motion.p>
            )}

            {desktop ? (
               <motion.div ref={trackRef} style={{ x }} className="relative flex w-max items-stretch gap-5 px-8 pr-[8vw]">
                  <MenuIntro desktop />
                  {COURSES.map((c) => (
                     <CourseCard key={c.id} c={c} />
                  ))}
               </motion.div>
            ) : (
               <>
                  <MenuIntro />
                  <CourseStack />
               </>
            )}

            {/* voortgang */}
            {desktop && (
               <div className="relative mx-auto mt-8 flex w-full max-w-[1400px] items-center gap-6 px-8">
                  <span className="w-28 font-ticket text-[11px] uppercase tracking-[0.18em] text-lp-ink">
                     {String(active + 1).padStart(2, "0")} / {String(COURSES.length).padStart(2, "0")}
                  </span>
                  <div className="h-px flex-1 bg-lp-ink/10">
                     <motion.div style={{ scaleX: scrollYProgress }} className="h-px origin-left bg-lp-ink" />
                  </div>
                  <span className="w-40 text-right font-editorial text-lg italic text-lp-ink">{COURSES[active].course}</span>
               </div>
            )}
         </div>
      </section>
   );
}
